<script setup lang="ts">
  import { useMediaControls } from '@vueuse/core'
  import type { VideoBannerStoryblok } from '@/storyblok/component-types-sb'
  import { optionalString, toAssetImageProps, toMultilinkHref } from '@/utils/storyblok/normalizers'

  const props = defineProps<{
    blok: VideoBannerStoryblok
  }>()

  const dayjs = useDayjs()

  const { external, slug, video, thumbnail, title, publishDate, autoplay, alt } = toRefs(props.blok)

  const url = external?.value ? toMultilinkHref(slug?.value) : optionalString(video?.value?.filename)
  const thumbnailProps = computed(() => toAssetImageProps(thumbnail.value))

  const videoElem = ref()
  const { playing, currentTime, muted } = useMediaControls(videoElem, {
    src: url
  })

  const isPlayed = ref(false)

  const handlePlay = () => {
    playing.value = true
  }

  const jsonLD = computed(() => ({
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: title.value,
    uploadDate: dayjs(publishDate.value).toISOString(),
    thumbnailUrl: [thumbnail.value?.filename],
    contentUrl: url
  }))

  const isEmbeddedVideo = computed(
    () => url?.includes('youtube.com') || url?.includes('youtu.be') || url?.includes('vimeo.com')
  )

  watch(currentTime, (newValue) => {
    if (newValue > 0) {
      isPlayed.value = true
    }
  })

  const runTimeConfig = useRuntimeConfig()

  const isValidVideo = computed(() => {
    if (external?.value) {
      return isEmbeddedVideo.value || url?.includes(runTimeConfig.public.btWebAssets)
    }
    return !!optionalString(video?.value?.filename)
  })

  const getEmbeddedUrl = () => {
    const includesQuery = url?.includes('?')
    const service = url?.includes('youtube.com') || url?.includes('youtu.be') ? 'youtube' : 'vimeo'
    let mutedParam = ''
    if (service === 'youtube') {
      mutedParam = `&mute=${autoplay?.value ? 1 : 0}`
    } else {
      mutedParam = `&muted=${autoplay?.value ? 1 : 0}`
    }
    const completeUrl = `${url}${includesQuery ? '&' : '?'}autoplay=1${mutedParam}`
    return completeUrl
  }

  useJsonLd(`video-banner-json-ld-${props.blok._uid}`, jsonLD)

  onMounted(() => {
    if (autoplay?.value) {
      if (videoElem.value) {
        muted.value = true
      }
      playing.value = true
    }
  })
</script>

<template>
  <div
    v-if="url && isValidVideo"
    v-editable="blok"
    class="relative aspect-video w-full overflow-hidden rounded-md"
  >
    <div class="relative aspect-video w-full overflow-hidden rounded-lg">
      <template v-if="external && isEmbeddedVideo">
        <iframe
          v-if="playing"
          width="100%"
          height="100%"
          :src="getEmbeddedUrl()"
          :title="alt"
          :aria-label="alt"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerpolicy="strict-origin-when-cross-origin"
          allowfullscreen
        />
        <NuxtImg
          v-if="!playing && !autoplay"
          :src="thumbnailProps.src"
          class="absolute left-0 top-0 h-full w-full object-cover"
          :aria-label="alt"
          :alt="alt"
        />
        <button
          v-if="!playing && !autoplay"
          class="absolute left-0 top-0 flex size-full cursor-pointer items-center justify-center"
          @click="handlePlay"
        >
          <Icon
            name="common/play"
            class="md-32 rounded-full bg-bt-green p-2"
          />
        </button>
      </template>
      <template v-else>
        <video
          ref="videoElem"
          :controls="isPlayed"
          class="aspect-video w-full object-cover"
          :poster="thumbnailProps.src"
          :title="alt"
          :aria-label="alt"
          playsinline
          controlslist="nodownload"
        />
        <button
          v-if="!isPlayed && !autoplay"
          class="absolute left-0 top-0 flex size-full cursor-pointer items-center justify-center"
          @click="handlePlay"
        >
          <Icon
            name="common/play"
            class="md-32 rounded-full bg-bt-green p-2"
          />
        </button>
      </template>
    </div>
  </div>
</template>
